import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Swal from 'sweetalert2';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay'; 
import { 
    FiX, FiClipboard, FiCalendar, FiBox, FiUser, FiTruck, 
    FiClock, FiTarget, FiTool, FiDroplet, FiAlertCircle, FiSearch, 
    FiDollarSign, FiFileText 
} from 'react-icons/fi';
import styles from './DetalhesProducaoModal.module.css'; 
import GerarRelatorioProducaoModal from './GerarRelatorioProducaoModal'; 

const formatDecimal = (value, decimals = 2) => {
    const numericValue = parseFloat(value) || 0;
    if (numericValue === 0) return '0'; 
    return numericValue.toFixed(decimals).replace('.', ',');
};

const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Remove hora se vier junto
    const cleanDate = String(dateString).split('T')[0].split(' ')[0];

    const [year, month, day] = cleanDate.split('-');

    if (!year || !month || !day) return dateString;

    return `${day}/${month}/${year}`;
};


const DetalhesProducaoModal = ({ equipeId, nomeEquipe, onClose, veiculos = [] }) => {
    const [detalhes, setDetalhes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroInicio, setFiltroInicio] = useState('');
    const [filtroFim, setFiltroFim] = useState('');
    const [filtroMaquinaId, setFiltroMaquinaId] = useState(''); 
    
    const [isGerarRelatorioOpen, setIsGerarRelatorioOpen] = useState(false);
    
    const icons = {
        data: FiCalendar, operador: FiUser, maquina: FiTruck,
        tipo_colheita: FiBox, producao: FiClipboard, horas: FiClock,
        parada: FiTool, local: FiTarget, diesel: FiDroplet,
        horimetro: FiClock, valor: FiDollarSign,
    };
    
    const renderDetalhe = (label, value, IconComponent) => (
        <div className={styles.detailItem}>
            <div className={styles.detailLabel}>
                <IconComponent size={14} />
                {label}
            </div>
            {value || '-'}
        </div>
    );

    const fetchDetalhes = useCallback(async (start, end, veiculoId) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            const params = { 
                dataInicio: start, 
                dataFim: end,
                veiculoId: veiculoId
            };
            
            const response = await axios.get(`${API_BASE_URL}/api/producao/equipes/${equipeId}/detalhes`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });
            
            setDetalhes(response.data);
        } catch (error) {
            console.error("Erro ao buscar detalhes da produção:", error);
            Swal.fire('Erro', 'Não foi possível carregar os registros de produção detalhados.', 'error');
            setDetalhes([]);
        } finally {
            setLoading(false);
        }
    }, [equipeId]);
    
    useEffect(() => {
        const today = new Date().toISOString().substring(0, 10);
        setFiltroFim(today);
        
        const threeMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().substring(0, 10);
        setFiltroInicio(threeMonthsAgo);
        
        fetchDetalhes(threeMonthsAgo, today, filtroMaquinaId); 
    }, [fetchDetalhes]);
    
    const handleBuscaInterna = () => {
         if (!filtroInicio || !filtroFim) {
             Swal.fire('Atenção', 'Preencha as duas datas (Início e Fim) para filtrar.', 'warning');
             return;
         }
         
         let veiculoIdParaBusca = filtroMaquinaId;
         
         if (veiculoIdParaBusca !== '') {
             veiculoIdParaBusca = parseInt(veiculoIdParaBusca);
             if (isNaN(veiculoIdParaBusca)) {
                 veiculoIdParaBusca = '';
             }
         }
         
         fetchDetalhes(filtroInicio, filtroFim, veiculoIdParaBusca);
    };
    
    const handleOpenGerarRelatorio = () => {
        if (!filtroInicio || !filtroFim) {
             Swal.fire('Atenção', 'Defina as datas de Início e Fim antes de gerar o relatório.', 'warning');
             return;
         }
        setIsGerarRelatorioOpen(true);
    }
    const handleCloseGerarRelatorio = () => setIsGerarRelatorioOpen(false);


    if (loading) {
        return (
            <div className={styles.overlay}>
                <div className={styles.modal}>
                    <LoadingOverlay text="Carregando Registros Diários..." isSmall={true} />
                </div>
            </div>
        );
    }

    const EmptyState = (
        <div className={styles.emptyState}>
            <FiClipboard size={40} />
            <p>Nenhum registro de produção encontrado para esta equipe no período {filtroMaquinaId ? 'para a máquina selecionada' : ''}.</p>
        </div>
    );

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        <FiClipboard /> Registros Brutos de Produção ({nomeEquipe})
                    </h2>
                    <button onClick={onClose} className={styles.closeButton}>
                        <FiX size={24} />
                    </button>
                </div>
                
                <div className={styles.filterRow}>
                    <div className={styles.filterGroup} style={{ flex: 2, minWidth: '150px' }}>
                        <label className={styles.filterLabel}>Máquina</label>
                        <select
                            value={filtroMaquinaId}
                            onChange={(e) => setFiltroMaquinaId(e.target.value)}
                            className={styles.selectFilter}
                            disabled={loading}
                        >
                            <option value="">-- Todas as Máquinas --</option>
                            {veiculos.map(v => (
                                <option key={v.id} value={v.id}>
                                    {v.identificacao} - {v.modelo}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup} style={{ flex: 1, minWidth: '120px' }}>
                        <label className={styles.filterLabel}>Data Início</label>
                        <input 
                            type="date"
                            value={filtroInicio}
                            onChange={(e) => setFiltroInicio(e.target.value)}
                            className={styles.filterInput}
                            disabled={loading}
                        />
                    </div>
                    <div className={styles.filterGroup} style={{ flex: 1, minWidth: '120px' }}>
                        <label className={styles.filterLabel}>Data Fim</label>
                        <input 
                            type="date"
                            value={filtroFim}
                            onChange={(e) => setFiltroFim(e.target.value)}
                            className={styles.filterInput}
                            disabled={loading}
                        />
                    </div>
                    <button 
                        onClick={handleBuscaInterna} 
                        className={styles.filterButton}
                        disabled={loading || !filtroInicio || !filtroFim}
                    >
                        <FiSearch size={16} /> Buscar
                    </button>
                    
                    <button 
                        onClick={handleOpenGerarRelatorio} 
                        className={styles.filterButton}
                        disabled={loading || !filtroInicio || !filtroFim}// Azul para diferenciar do buscar
                    >
                        <FiFileText size={16} /> Gerar Relatório
                    </button>
                </div>
                
                <p style={{ color: 'var(--color-text-secondary)', marginBottom: '20px', fontWeight: '600' }}>
                    Total de {detalhes.length} registros encontrados no período.
                </p>
                
                {detalhes.length === 0 ? (
                    EmptyState
                ) : (
                    detalhes.map((d) => (
                        <div key={d.id} className={styles.registroCard}>
                            
                            {renderDetalhe("Data", formatDate(d.data), icons.data)}
                            {renderDetalhe("Operador", d.operador_username, icons.operador)}
                            {renderDetalhe("Máquina", `${d.veiculo_modelo} (${d.veiculo_identificacao})`, icons.maquina)}
                            {renderDetalhe("Tipo de Serviço", d.tipo_colheita, icons.tipo_colheita)}
                            
                            {renderDetalhe("H. Inicial", d.horimetro_inicial, icons.horimetro)}
                            {renderDetalhe("H. Final", d.horimetro_final, icons.horimetro)}s
                            {renderDetalhe("Produção (Valor Bruto)", d.producao_diaria_valor, icons.producao)}
                            {renderDetalhe("Local Colheita", d.local_colheita, icons.local)}
                            {renderDetalhe("Talhão", d.talhao_colheita, icons.data)}
                            
                            {d.motivo_parada && renderDetalhe("Motivo Parada", d.motivo_parada, icons.parada)}
                            {d.horas_paradas && renderDetalhe("Horas Paradas", `${d.horas_paradas} h`, icons.horas)}
                            
                            {d.num_arvores_cortadas && renderDetalhe("Árvores Cortadas", d.num_arvores_cortadas, icons.producao)}
                            {d.qtde_julieta_bitren && renderDetalhe("Qtde Julieta/Bitren", d.qtde_julieta_bitren, icons.producao)}
                            {d.qtde_truck && renderDetalhe("Qtde Truck", d.qtde_truck, icons.producao)}

                            {d.diesel_litros && renderDetalhe("Diesel (L)", `${d.diesel_litros} L`, icons.diesel)}
                            {d.hidraulico_litros && renderDetalhe("Hidráulico (L)", `${d.hidraulico_litros} L`, icons.diesel)}
                            {d.corrente_litros && renderDetalhe("Corrente (L)", `${d.corrente_litros} L`, icons.diesel)}
                            {d.transmissao_litros && renderDetalhe("Transmissão (L)", `${d.transmissao_litros} L`, icons.diesel)}
                            {d.motor_litros && renderDetalhe("Motor (L)", `${d.motor_litros} L`, icons.diesel)}


                            {d.nota_relevante && (
                                <div className={styles.note}>
                                    <div className={styles.detailLabel}>
                                        <FiAlertCircle size={14} style={{ color: 'var(--color-alert-note)' }}/>
                                        Observação Relevante:
                                    </div>
                                    {d.nota_relevante}
                                </div>
                            )}

                        </div>
                    ))
                )}
            </div>
            
            {isGerarRelatorioOpen && (
                 <GerarRelatorioProducaoModal
                    equipeId={equipeId}
                    nomeEquipe={nomeEquipe}
                    veiculos={veiculos}
                    onClose={handleCloseGerarRelatorio}
                    filtroInicio={filtroInicio}
                    filtroFim={filtroFim}
                    filtroMaquinaId={filtroMaquinaId}
                 />
            )}
        </div>
    );
};

export default DetalhesProducaoModal;